import React from 'react';
import { motion } from 'framer-motion';
import { useCourse } from '../../context/useCourseContext';
import { useCourseData } from '../../pages/Course';

// UI & Icons
import { Button } from '@/components/ui/button'; // Assuming shadcn/ui Button
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'; // Assuming shadcn/ui Dropdown
import {
  BookCopy,
  BookOpenText,
  ScanEye,
  Sparkles,
  MessagesSquare,
  Clipboard,
  PanelLeftClose,
  PanelLeftOpen,
  ChevronDown,
} from 'lucide-react';
import Settings from './components/Settings'
import VERSIONS from '@assets/js/versions';

function Navigation() {
  const { selectedTopic, selectedToggle, setSelectedToggle, expanded, setExpanded } = useCourse();
  const { course, isAdmin } = useCourseData();

  // --- Core Logic (Shared between Mobile & Desktop) ---

  const appVersion = VERSIONS[process.env.REACT_APP_VERSION];
  const isEdunexora = appVersion.brand === 'Edunexora';
  const featureMap = {
    docchat: { label: 'AI Chat', icon: Sparkles, featureKey: 'ai_chat' },
    community: { label: 'Community', icon: MessagesSquare, featureKey: 'community' },
    flashcard: { label: 'FlashCards', icon: BookOpenText, featureKey: 'flashcards' },
    quiz: { label: 'Practice', icon: Clipboard, featureKey: 'quiz' },
    summary: { label: 'Glossary', icon: ScanEye, featureKey: 'summary' },
  };

  const toggleItems = Object.entries(featureMap)
    .filter(([key, { featureKey }]) => {
      if (isEdunexora && key === 'community') return false;
      return course?.features_enabled?.[featureKey];
    })
    .map(([key, { label, icon }]) => ({
      value: key,
      label,
      icon,
    }));

  const navItems = [...(selectedTopic ? [{ value: 'topic', label: 'Topic', icon: BookCopy }] : []), ...toggleItems];

  const activeValue = selectedToggle || (selectedTopic ? 'topic' : null);
  const activeItem = navItems.find((item) => item.value === activeValue);

  const handleNavClick = (value) => {
    if (value === 'topic') {
      setSelectedToggle(null);
    } else {
      setSelectedToggle(value);
    }
  };

  // --- Render ---

  return (
    <div className="sticky top-0 z-30 w-full border-b border-border/40 bg-muted/40 backdrop-blur-sm">
      <div className="flex items-center justify-between w-full gap-2 px-2 py-1">
        {/* Left-Side Controls (Always Visible) */}
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            aria-label={expanded ? 'Collapse Sidebar' : 'Expand Sidebar'}
            onClick={() => setExpanded(!expanded)}>
            {expanded ? <PanelLeftClose size={18} /> : <PanelLeftOpen size={18} />}
          </Button>
        </div>

        {/* Desktop Navigation (Visible on md screens and up) */}
        <div className="hidden md:flex items-center gap-2">
          {navItems.length > 0 && (
            <div className="relative flex items-center p-1 space-x-1 border rounded-lg bg-muted">
              {navItems.map(({ value, label, icon: Icon }) => (
                <button
                  key={value}
                  onClick={() => handleNavClick(value)}
                  className={`relative z-10 flex items-center px-3 py-1.5 text-sm font-medium transition-colors ${
                    activeValue === value ? 'text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                  }`}>
                  {activeValue === value && (
                    <motion.div
                      layoutId="active-nav-pill"
                      className="absolute inset-0 z-0 rounded-md bg-primary"
                      transition={{ type: 'spring', stiffness: 400, damping: 35 }}
                    />
                  )}
                  <Icon size={16} className="relative z-10 mr-2" />
                  <span className="relative z-10">{label}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Mobile Navigation (Hidden on md screens and up) */}
        <div className="flex-grow md:hidden">
          {activeItem && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="w-full flex justify-center items-center gap-2">
                  <activeItem.icon size={16} />
                  <span>{activeItem.label}</span>
                  <ChevronDown size={16} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="w-[--radix-dropdown-menu-trigger-width]]">
                {navItems.map(({ value, label, icon: Icon }) => (
                  <DropdownMenuItem key={value} onSelect={() => handleNavClick(value)}>
                    <Icon className="mr-2 h-4 w-4" />
                    <span>{label}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {/* Right-Side Controls (Always Visible) */}
        <div className="flex items-center gap-1">{isAdmin && <Settings course={course} />}</div>
      </div>
    </div>
  );
}

export default Navigation;
